import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, LoaderCircle, MapPin, ScanFace, Smartphone, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LanguageToggle } from "@/components/LanguageToggle";
import { BrandMark } from "@/components/Brand";
import { InstallApp } from "@/components/InstallApp";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";

/* Apple-clean login: generous whitespace, bold purposeful identity, quiet
   feature summary. Zero external assets — system fonts and inline SVG only. */
export default function LoginPage() {
  const { login } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const user = await login(email.trim(), password);
      navigate(user.mustChangePassword ? "/change-password" : "/", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      setError(status === 423 ? t("accountLocked") : t("loginFailed"));
    } finally {
      setBusy(false);
    }
  };

  const features = [
    { icon: ScanFace, key: "loginFeatureFace" },
    { icon: MapPin, key: "loginFeatureGps" },
    { icon: ShieldCheck, key: "loginFeatureReview" },
    { icon: Smartphone, key: "loginFeaturePwa" },
  ];

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="flex items-center justify-between px-6 py-5 sm:px-10">
        <span className="inline-flex items-center gap-2.5" data-testid="login-brand">
          <BrandMark className="h-8 w-8" />
          <span className="text-lg font-semibold tracking-tight">
            Rajin<span className="text-primary">Kerja</span>
          </span>
        </span>
        <LanguageToggle />
      </header>

      <main className="mx-auto grid w-full max-w-5xl flex-1 items-center gap-12 px-6 pb-16 pt-6 sm:px-10 lg:grid-cols-2">
        {/* identity + about */}
        <section className="order-2 lg:order-1">
          <h1 data-testid="login-title" className="text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">
            {t("loginHeadline")}
          </h1>
          <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
            {t("loginSubline")}
          </p>
          <ul className="mt-8 space-y-4">
            {features.map(({ icon: Icon, key }) => (
              <li key={key} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <Icon className="h-4 w-4 text-primary" />
                </span>
                <span className="text-sm leading-6 text-foreground/90">{t(key)}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* sign-in card */}
        <section className="order-1 lg:order-2">
          <div className="mx-auto w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-sm">
            <h2 className="text-xl font-semibold tracking-tight">{t("login")}</h2>
            <p className="mt-1 text-sm text-muted-foreground">{t("signInToContinue")}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email">{t("email")}</Label>
                <Input
                  id="email" type="email" autoComplete="username" required
                  data-testid="login-email-input"
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  className="h-12 rounded-xl"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password">{t("password")}</Label>
                <div className="relative">
                  <Input
                    id="password" type={showPw ? "text" : "password"} autoComplete="current-password" required
                    data-testid="login-password-input"
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    className="h-12 rounded-xl pr-11"
                  />
                  <button
                    type="button"
                    data-testid="login-password-toggle"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                    aria-label="toggle password visibility"
                  >
                    {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {error && (
                <p data-testid="login-error" className="text-sm font-medium text-destructive">{error}</p>
              )}
              <Button type="submit" disabled={busy} data-testid="login-form-submit-button"
                className="h-12 w-full rounded-xl text-base">
                {busy ? (<><LoaderCircle className="mr-2 h-4 w-4 animate-spin" />{t("loggingIn")}</>) : t("login")}
              </Button>
            </form>
            <p className="mt-5 text-center text-xs text-muted-foreground">{t("loginInternalNote")}</p>
          </div>
          <InstallApp />
        </section>
      </main>

      <footer className="flex items-center justify-center gap-6 border-t border-border px-6 py-5 text-xs text-muted-foreground">
        <Link to="/terms" data-testid="footer-terms-link" className="transition-colors duration-150 hover:text-foreground">
          {t("termsOfUse")}
        </Link>
        <span aria-hidden="true">·</span>
        <Link to="/privacy" data-testid="footer-privacy-link" className="transition-colors duration-150 hover:text-foreground">
          {t("privacyPolicy")}
        </Link>
      </footer>
    </div>
  );
}
